import QuizManager from "../quizes/quiz-manager";
import cloneObject from "../../editor/helpers/cloneObject";

const CAMERA_LIMITS_VERSION = 4;
const CONTROLS_VERSION = 1;

class Q3DataStructure {
    constructor() {
        this.labels = new DataLabels();
        this.assets = new DataAssets();
        this.layers = new DataLayers();
        this.setups = new DataSetups();
        this.liveLabels = new DataLivelabels();
        this.quizData = {};
        this.slides = [new DataSlide()];
        this.s3Id = "assets";
        this.highlightColor = new THREE.Color("#77ff77").getHex();
    }

    updateLayers() {
        Object.keys(Q3.layers.list).forEach(layerKey => {
            const layer = Q3.layers.list[layerKey];
            if (layer.subtype === "graphers") {
                layer.objects.forEach(object => {

                    if (object.type2d === "Graphing") {
                        const dtoLayer = this.layers.layers2D[layerKey];
                        const dto = dtoLayer.objects[object.uuid];

                        Object.assign(dto, object.toJSON());
                    }
                });
            }
        });
    }

    toJSON() {
        this.updateLayers();

        return this;
    }

    deserialize(data, noControls) {
        Object.assign(this, data);

        this.setups.autoplayDelay = 10;
        if (noControls) this.slides[0].hideControls = true;

        this.slides.forEach(slide => {
            let objectRotationMode = 'orbit';

            if (!slide.cameraLimits) {
                slide.cameraLimits = {
                    version: CAMERA_LIMITS_VERSION,
                    zoomLimitEnabled: false,
                    minDistance: 3,
                    maxDistance: 7,
                    horizontalAngleLimitEnabled: false,
                    minAzimuthAngle: 0,
                    maxAzimuthAngle: 90,
                    verticalAngleLimitEnabled: false,
                    minPolarAngle: 0,
                    maxPolarAngle: 90,
                    panLimitEnabled: false,
                    panLimits: {
                        center: { x: 0, y: 0, z: 0 },
                        quaternion: { x: 0, y: 0, z: 0, w: 1 },
                        offset: 0,
                    },
                    objectScaleLimitEnabled: false,
                    objectScaleLimit: 2,
                }
            } else {
                let limits = slide.cameraLimits;
                let version = limits.version || 0;

                if (version < 1) {
                    if (!limits.zoomLimitEnabled && limits.minDistance === 0 && limits.maxDistance === 10) {
                        limits.minDistance = 3;
                        limits.maxDistance = 7;
                    }

                    if (!limits.horizontalAngleLimitEnabled && limits.minAzimuthAngle === -180 && limits.maxAzimuthAngle === 180) {
                        limits.minAzimuthAngle = 0;
                        limits.maxAzimuthAngle = 90;
                    }
                }

                if (version < 2) {
                    if (!limits.enablePan) {
                        limits.panLimitEnabled = true;
                        limits.panLimits = {
                            center: Object.assign({}, slide.camera.target),
                            quaternion: { x: 0, y: 0, z: 0, w: 1 },
                            offset: 0,
                        };
                    }
                }

                if (version < 3) {
                    limits.horizontalObjectRotationOnly = true;
                    limits.objectScaleLimitEnabled = false;
                    limits.objectScaleLimit = 2;
                }

                if (version < 4) {
                    objectRotationMode = limits.horizontalObjectRotationOnly ? 'horizontal' : 'trackball';
                    delete limits.horizontalObjectRotationOnly;
                }

                limits.version = CAMERA_LIMITS_VERSION;
            }

            if (!slide.controls) {
                slide.controls = {
                    version: CONTROLS_VERSION,
                    objectRotationMode,
                    interactionType:  "objectRotation",
                };
            } else {
                let controls = slide.controls;
                let version = controls.version || 0;

                if (version < 1) {
                    controls.objectRotationMode = objectRotationMode;
                }
                controls.interactionType = controls.interactionType || "objectRotation";
                controls.version = CONTROLS_VERSION;
            }

            if (!('visible' in slide)) { slide.visible = true; }
            if (!('title' in slide)) { slide.title = "Slide"; }

            if (!slide.envLight) {
                slide.envLight = this.assets.envLight || '/assets-qbix/F16EFB92-4B2C-46EB-8F86-8AC2322E4FAE.env';
            }

            if (!slide.fog) { slide.fog = this.setups.fog; }
        });
        QuizManager.initQuizes(this);
        try {
            if (!this.slides[0].layers2D) {
                let newLayers2D = {};

                this.slides.forEach(slideData => {
                    let uuidMap = {};

                    slideData.layers2D = {};

                    Object.keys(this.layers.layers2D).forEach(uuid => {
                        if ((typeof slideData.layers[uuid]) !== "undefined") {
                            if (slideData.layers[uuid] === true) {
                                let newUuid = THREE.Math.generateUUID();
                                uuidMap[uuid] = newUuid;
                                newLayers2D[newUuid] = cloneObject(this.layers.layers2D[uuid]);
                                slideData.layers2D[newUuid] = 1;
                                slideData.layers[newUuid] = true;
                                delete slideData.layers[uuid];
                                Object.keys(newLayers2D[newUuid].objects).forEach(objUuid => {
                                    uuidMap[objUuid] = THREE.Math.generateUUID();
                                    newLayers2D[newUuid].objects[uuidMap[objUuid]] = cloneObject(newLayers2D[newUuid].objects[objUuid]);
                                    delete newLayers2D[newUuid].objects[objUuid];
                                });
                            }
                        }
                    });
                });

                this.layers.layers2D = newLayers2D;
            }
        } catch (err) {
            console.log(err);
        }

        return this;
    }
}

export default Q3DataStructure;
export { Q3DataStructure };

class DataLabels {
    constructor() {
        this.hover = { enabled: true };
        this.number = {
            enabled: false,
            quizzing: false
        };
    }
}

class DataAssets {
    constructor() {
        this.textures = {};
        this.materials = {};
        this.geometries = {};
        this.objects = {};
        this.lights = {};
        this.animations = {};
        this.skeletons = {};
        this.audio = {};
    }
}

class DataLayers {
    constructor() {
        this.layer3D = {
            index: 0,
            name: "Layer 3D"
        };
        this.layers2D = {};
    }
}

class DataSlide {
    constructor() {
        this.uuid = THREE.Math.generateUUID();
        this.labels = {
            number: {
                position: {},
                description: "Description of the first slide",
                question: "Question of the first slide"
            }
        };
        this.camera = {
            position: { x: 2.5, y: 0, z: 2.5 },
            target: { x: 0, y: 0, z: 0 },
            zoom: 1,
            fov: 45
        };
        this.renderer = { left: 0, top: 0, width: 1, height: 1 };
        this.layers = { Renderer: true };
        this.animation = {};
        /**
         * @type {import("../quizes/slide-quiz").SlideQuiz?}
         */
        this.slideQuiz = null;
        this.envLight = "/assets-qbix/F16EFB92-4B2C-46EB-8F86-8AC2322E4FAE.env";
        this.background = {
            type: "none",
            value: {
                color: {
                    primary: "#6932ff",
                    secondary: "#083f8f",
                    gradient: {
                        enabled: true,
                        style: "radial"
                    }
                }
            }
        };
    }
}

class DataSetups {
    constructor() {
        this.autoplayDelay = 10;
        this.toneMapping = 1;
        this.toneMappingExposure = 1;
        this.gammaInput = true;
        this.gammaOutput = true;

        this.postprocess = {
            fxaa: true,
            ssaa: true,
            bloom: false,
            SAO: false
        };

        this.background = "0x000000";
        this.fog = {
            enabled: false,
            color: "0xffffff",
            near: 0,
            far: 100
        };

        this.controls = {
            enableDamping: true,
            zoomSpeed: 0.18,
            rotateSpeed: 0.1,
            panSpeed: 0.2,
            zoomDampingFactor: 0.07,
            panDampingFactor: 0.08,
            rotateDampingFactor: 0.08
        };

        this.camera = {
            fov: 45,
            near: 0.1,
            far: 200
        };
    }
}

class DataLivelabels {
    constructor() { }
}

class DataAudio {
    constructor() { }
}

export { DataSlide };
